import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import useAuthStore from "@/store/use-auth-store";
import { useDebounce } from "@/hooks/use-debounce";

import { CONTACT_SOURCE } from "../config/support-config";
import { isAdminUser } from "../utils/admin";
import type {
  SupportTicketListResponse,
  SupportTicketResponse,
  SupportTicketStatsResponse,
} from "../types";

const TICKETS_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

export type TicketStatusFilter =
  | "all"
  | "open"
  | "in_progress"
  | "solved"
  | "on_hold"
  | "closed";

/**
 * Kullanıcının destek taleplerini sayfalı olarak getirir.
 *
 * get-all-contact auth gerektirir; backend admin'e tüm ticket'ları,
 * `scope=mine` ile yalnızca kendininkileri döndürür (normal kullanıcıda
 * scope yok sayılır, hep kendi ticket'ları gelir). Admin görünümünde
 * kapsam URL'deki `?scope=all` parametresiyle belirlenir; parametre yoksa
 * admin de "My Tickets" sekmesinde yalnızca kendi taleplerini görür.
 * Cevap bekleyen (unread) ticket'lar backend tarafından başa sıralanır.
 */
export const useMyTickets = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilterState] =
    useState<TicketStatusFilter>("all");
  const [search, setSearchState] = useState("");
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  const isAdmin = isAdminUser(user);
  const isAllScope = isAdmin && searchParams.get("scope") === "all";

  const query = useQuery({
    queryKey: ["support-tickets", isAllScope, page, statusFilter, debouncedSearch],
    queryFn: () =>
      apiClient.get<SupportTicketListResponse>(API_ENDPOINTS.getAllContact, {
        params: {
          page,
          limit: TICKETS_PAGE_SIZE,
          // Admin "My Tickets" sekmesindeyken yalnızca kendi talepleri
          ...(isAdmin && !isAllScope && { scope: "mine" }),
          ...(statusFilter !== "all" && { status: statusFilter }),
          ...(debouncedSearch && { search: debouncedSearch }),
        },
      }),
    enabled: !!user?._id,
    staleTime: 30_000,
    // Filtre/sayfa değişiminde liste boşalıp yanıp sönmesin
    placeholderData: keepPreviousData,
  });

  // Sekme (kapsam) değişince sayfalama ve filtreler baştan başlar
  useEffect(() => {
    setPage(1);
    setStatusFilterState("all");
    setSearchState("");
  }, [isAllScope]);

  const setStatusFilter = (value: TicketStatusFilter) => {
    setStatusFilterState(value);
    setPage(1);
  };

  const setSearch = (value: string) => {
    setSearchState(value);
    setPage(1);
  };

  const tickets = query.data?.data?.result ?? [];
  const meta = query.data?.data?.meta;

  return {
    tickets,
    meta,
    page,
    setPage,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    isAdmin,
    isAllScope,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError || query.data?.success === false,
    refetch: query.refetch,
  };
};

/**
 * Ticket istatistikleri (admin only) — toplam, statü kırılımı ve
 * cevap bekleyen (unread) sayısı. Sidebar rozeti ve liste üstündeki
 * filtre çipleri aynı cache'ten beslenir; 60 sn'de bir tazelenir.
 */
export const useTicketStats = () => {
  const { user } = useAuthStore();
  const isAdmin = isAdminUser(user);

  const query = useQuery({
    queryKey: ["support-ticket-stats"],
    queryFn: () =>
      apiClient.get<SupportTicketStatsResponse>(API_ENDPOINTS.getContactStats),
    enabled: !!user?._id && isAdmin,
    staleTime: 30_000,
    refetchInterval: 60_000,
    // Rol tespiti yanlışsa (403) sessizce vazgeç, tekrar deneme
    retry: false,
  });

  return {
    stats: query.data?.data ?? null,
    isAdmin,
    isLoading: query.isLoading,
  };
};

interface ReplyPayload {
  body: string;
  images: File[];
}

/**
 * Tek ticket detayı + cevap gönderme (görsel ekli).
 *
 * Magic link ile gelen misafirler URL'deki `?token=...` ile yetkilendirilir;
 * okuma isteğinde query param, cevap isteğinde multipart form alanı olarak
 * iletilir (backend POST'ta token'ı form gövdesinde bekler).
 * Görseller multipart içinde dosya olarak gönderilir; R2 yüklemesini backend
 * yapar, böylece başarısız istekte orphan asset oluşmaz.
 */
export const useTicketDetail = (ticketId: string | undefined) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("token");

  // Token varsa isteklere iletilir; login kullanıcıda Authorization yeterli
  const tokenParams = magicToken ? { token: magicToken } : undefined;
  const queryKey = ["support-ticket", ticketId, magicToken];

  const query = useQuery({
    queryKey,
    queryFn: () =>
      apiClient.get<SupportTicketResponse>(
        API_ENDPOINTS.getSingleContact(ticketId!),
        { params: tokenParams },
      ),
    enabled: !!ticketId,
  });

  const replyMutation = useMutation({
    mutationFn: async ({ body, images }: ReplyPayload) => {
      // Görseller dosya olarak gönderilir; R2 yüklemesini backend yapar
      const formData = new FormData();
      formData.append("body", body);
      formData.append("source", CONTACT_SOURCE);

      // Backend, misafir yetkilendirmesinde token'ı multipart form alanı
      // olarak bekliyor (query param yalnızca GET'te geçerli)
      if (magicToken) {
        formData.append("token", magicToken);
      }

      const locale = i18n.language?.split("-")[0];
      if (locale) {
        formData.append("locale", locale);
      }

      images.forEach((file) => formData.append("images", file));

      const response = await apiClient.post<SupportTicketResponse>(
        API_ENDPOINTS.addContactMessage(ticketId!),
        formData,
        { params: tokenParams },
      );

      if (!response?.success) {
        throw new Error(response?.message || "Reply failed");
      }
      return response;
    },
    onSuccess: (response) => {
      if (response.data) {
        // Detay cache'ini güncel ticket ile doğrudan güncelle
        queryClient.setQueryData(queryKey, response);
      }
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      // Admin cevap yazınca unread sayısı değişir → rozet güncellensin
      queryClient.invalidateQueries({ queryKey: ["support-ticket-stats"] });
    },
  });

  // Durum güncelleme — admin herhangi bir statüye çekebilir; kullanıcı
  // kendi talebini kapatırken de aynı mutasyon "closed" ile kullanılır
  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      const response = await apiClient.put<SupportTicketResponse>(
        API_ENDPOINTS.updateContactStatus(ticketId!),
        { status },
        { params: tokenParams },
      );

      if (!response?.success) {
        throw new Error(response?.message || "Status update failed");
      }
      return response;
    },
    onSuccess: (response) => {
      if (response.data) {
        queryClient.setQueryData(queryKey, response);
      }
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support-ticket-stats"] });
    },
  });

  return {
    ticket: query.data?.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError || query.data?.success === false,
    refetch: query.refetch,

    sendReply: replyMutation.mutateAsync,
    isReplying: replyMutation.isPending,

    updateStatus: statusMutation.mutateAsync,
    isUpdatingStatus: statusMutation.isPending,

    closeTicket: () => statusMutation.mutateAsync("closed"),
    isClosing: statusMutation.isPending,
  };
};
